<script setup lang="ts">
import type { ItemType } from '~/types'

const favourites = useFavouritesStore()

type Props = {
  id: string
  type: ItemType
  to: string
  title: string
  image: {
    src?: string | null
    alt?: string | null
  }
  favourited?: string | null
  refetch?: () => Promise<unknown>
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  favourited: undefined,
  refetch: undefined,
  class: undefined,
})

const classes = computed(() => ({
  'app-card': true,
  [`${props.class}`]: !!props.class,
}))

const toggleFavourite = async (event: MouseEvent) => {
  event.preventDefault()

  await favourites.toggleFavourite({
    id: props.id,
    type: props.type,
    favouriteId: props.favourited,
  })

  if (props.refetch) await props.refetch()
}
</script>

<template>
  <article :class="classes">
    <NuxtLink class="app-card__link" :to="to">
      <AppImage
        class="app-card__image"
        ratio="2:3"
        :src="image.src"
        :alt="image.alt"
      >
        <AppButtonLike
          class="app-card__like-button"
          :active="!!favourited"
          @click="toggleFavourite"
        />
      </AppImage>

      <div class="app-card__content">
        <h3
          class="app-card__content-title h4 line-clamp-3 text-balance"
          :title="title"
        >
          {{ title }}
        </h3>

        <div class="app-card__content-text clean-cms-styles">
          <slot></slot>
        </div>
      </div>
    </NuxtLink>
  </article>
</template>

<style lang="scss">
@use 'sass:math';

@import '@boats/ui/styles/config';

.app-card__image {
  margin-bottom: 0.75rem;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    background-color: $black;
    opacity: 0;
    @include transition('opacity');
  }

  img {
    @include transition('transform');
  }
}

.app-card__content-title {
  margin-bottom: 0.25rem;

  @include media-breakpoint-up(md) {
    font-size: 1.375rem;
  }
}

.app-card__content-text {
  p {
    font-family: $font-barlow;
    font-size: 0.875rem;
    line-height: 1.42857143;
    margin: 0;
  }

  @include media-breakpoint-up(md) {
    p {
      font-size: 1rem;
      line-height: 1.5;
    }
  }
}

.app-card__like-button {
  display: none;

  @include media-breakpoint-up(md) {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    display: block;
    z-index: 1;
  }
}

.app-card__link {
  display: block;
  text-decoration: none !important;

  &:hover {
    .app-card__image {
      img {
        transform: scale(1.05);
      }

      &::after {
        opacity: 0.1;
      }
    }

    .app-card__content-title {
      text-decoration: underline;
    }
  }

  &:active {
    .app-card__image {
      &::after {
        opacity: 0.65;
      }
    }
  }
}
</style>
